import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Share2, ExternalLink, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import SEO from '../components/common/SEO';
import { TechLabel } from '../components/common/TacticalCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UseCaseDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language, t, getLocalizedField } = useLanguage();
  const { getSetting } = useSiteSettings();
  
  const [usecase, setUseCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedCases, setRelatedCases] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch the specific use case
        const response = await fetch(`${API_URL}/api/usecases/${slug}`);
        if (!response.ok) {
          navigate('/404');
          return;
        }
        const data = await response.json();
        setUseCase(data.usecase);

        // Fetch related use cases
        const relatedResponse = await fetch(`${API_URL}/api/usecases`);
        const relatedData = await relatedResponse.json();
        // Filter out current and get up to 3 related
        const related = (relatedData.usecases || [])
          .filter(uc => uc.usecase_id !== data.usecase.usecase_id)
          .slice(0, 3);
        setRelatedCases(related);
      } catch (error) {
        console.error('Error fetching use case:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, navigate]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: getLocalizedField(usecase, 'title'),
          text: getLocalizedField(usecase, 'description'),
          url: window.location.href
        });
      } catch (err) {
        console.log('Share failed:', err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert(language === 'en' ? 'Link copied to clipboard!' : 'Link panoya kopyalandı!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-vav-bg pt-20 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-vav-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!usecase) {
    return (
      <div className="min-h-screen bg-vav-bg pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl text-vav-fg mb-4">404</h1>
          <p className="text-vav-fg-muted mb-6">{t('Kullanım alanı bulunamadı', 'Use case not found')}</p>
          <Link to="/" className="tactical-btn">{t('Ana Sayfaya Dön', 'Back to Home')}</Link>
        </div>
      </div>
    );
  }

  const title = getLocalizedField(usecase, 'title');
  const description = getLocalizedField(usecase, 'description');
  const content = getLocalizedField(usecase, 'content') || description;
  const seoTitle = usecase.seo_title || title;
  const seoDescription = usecase.seo_description || description;

  return (
    <>
      <SEO 
        title={`${seoTitle} | VAV Wear`}
        description={seoDescription}
        image={usecase.image_url}
        type="article"
      />
      
      <main className="min-h-screen bg-vav-bg pt-20">
        {/* Hero Section */}
        <section className="relative h-[50vh] md:h-[60vh] overflow-hidden">
          {/* Background Image/Video */}
          {usecase.video_url ? (
            <video
              src={usecase.video_url}
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : usecase.image_url ? (
            <img
              src={usecase.image_url}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-vav-bg-paper to-vav-bg" />
          )}
          
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-vav-bg via-vav-bg/60 to-transparent" />
          
          {/* Content */}
          <div className="absolute inset-0 flex flex-col justify-end">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                {/* Breadcrumb */}
                <nav className="flex items-center gap-2 text-sm text-vav-fg-muted mb-4">
                  <Link to="/" className="hover:text-vav-gold transition-colors">
                    {t('Ana Sayfa', 'Home')}
                  </Link>
                  <ChevronRight className="w-4 h-4" />
                  <Link to="/#use-cases" className="hover:text-vav-gold transition-colors">
                    {t('Kullanım Alanları', 'Use Cases')}
                  </Link>
                  <ChevronRight className="w-4 h-4" />
                  <span className="text-vav-fg">{title}</span>
                </nav>

                <TechLabel variant="accent" className="mb-4">
                  [{t('KULLANIM ALANI', 'USE CASE')}]
                </TechLabel>
                
                <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-vav-fg mb-4">
                  {title}
                </h1>
                
                <p className="text-lg text-vav-fg-muted max-w-2xl">
                  {description}
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <Link 
                to="/"
                className="inline-flex items-center gap-2 text-vav-fg-muted hover:text-vav-gold transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('Geri Dön', 'Go Back')}
              </Link>
              
              <div className="flex items-center gap-3">
                <button
                  onClick={handleShare}
                  className="p-2 rounded-lg border border-vav-border hover:border-vav-gold text-vav-fg-muted hover:text-vav-gold transition-colors"
                  title={t('Paylaş', 'Share')}
                >
                  <Share2 className="w-5 h-5" />
                </button>
                
                {usecase.link && (
                  <a
                    href={usecase.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tactical-btn inline-flex items-center gap-2"
                  >
                    {t('Mağazaya Git', 'Visit Store')}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>

            {/* Article Content */}
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="prose prose-invert prose-lg max-w-none"
            >
              <div 
                className="text-vav-fg-muted leading-relaxed space-y-6"
                dangerouslySetInnerHTML={{ __html: content.replace(/\n/g, '<br/>') }}
              />
            </motion.article>

            {/* Gallery */}
            {usecase.gallery && usecase.gallery.length > 0 && (
              <div className="mt-12">
                <h2 className="font-heading text-2xl text-vav-fg mb-6">
                  {t('Galeri', 'Gallery')}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {usecase.gallery.map((img, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.1 * idx }}
                      className="aspect-square rounded-xl overflow-hidden"
                    >
                      <img 
                        src={img} 
                        alt={`${title} - ${idx + 1}`}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Related Use Cases */}
        {relatedCases.length > 0 && (
          <section className="py-16 bg-vav-bg-paper border-t border-vav-border">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <TechLabel variant="gold" className="mb-3">
                  [{t('DİĞER ALANLAR', 'OTHER AREAS')}]
                </TechLabel>
                <h2 className="font-heading text-3xl text-vav-fg">
                  {t('İlgili Kullanım Alanları', 'Related Use Cases')}
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedCases.map((related, idx) => (
                  <motion.div
                    key={related.usecase_id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <Link
                      to={`/use-cases/${related.slug || related.usecase_id}`}
                      className="block group"
                    >
                      <div className="relative aspect-video rounded-xl overflow-hidden mb-4">
                        {related.image_url ? (
                          <img
                            src={related.image_url}
                            alt={getLocalizedField(related, 'title')}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full bg-vav-bg-subtle flex items-center justify-center">
                            <span className="text-4xl text-vav-fg-dark">{related.icon || '🏔️'}</span>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      </div>
                      <h3 className="font-heading text-lg text-vav-fg group-hover:text-vav-gold transition-colors">
                        {getLocalizedField(related, 'title')}
                      </h3>
                      <p className="text-sm text-vav-fg-muted mt-2 line-clamp-2">
                        {getLocalizedField(related, 'description')}
                      </p>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
};

export default UseCaseDetail;
